import type { SlideLayoutTemplate, SlideOverlayStyle, SlideTheme } from '../../types/api';

/**
 * The renderer's caption layouts, as much of them as CSS can say.
 *
 * Mirrors of the backend render template (app/rendering/spec.py and
 * app/rendering/render.py): canvas 1080, safe margin 80, scrim fade 160,
 * scrim alpha 110/255 with the corrective boost at 175/255, text colours
 * #F7F5F0 / #1C1B18, frosted blur 14 with its tint at 70/255, card radius
 * 32 and fill alpha 200/255, eyebrow 26px with 3px tracking and an 18px gap.
 * Change one side, change the other.
 *
 * The mirror is now a table, exactly as `TEMPLATE_GEOMETRY` is on the server:
 * every template carries its own text column, the vertical region that column
 * is placed inside, the picture slot beside it (or none), the first rung of its
 * headline ladder, its body size and whether its headline is upper-cased. Three
 * families come out of that — `anchor` (caption on a full-bleed picture),
 * `slot` (picture fitted into its own box on a flat panel, caption beside it)
 * and `content` (`quote` and `stat`, which shape the type rather than the
 * frame). What the preview cannot mirror is the ladder itself: the renderer
 * steps the type down when the words do not fit and the browser does not, which
 * is one more thing the "preview approximate" line under the stage is for.
 *
 * Everything here is a fraction of the square slide or a percentage of it, the
 * same units `text_position` travels in, so the preview and the PNG mean the
 * same thing at any size on screen.
 */

/** The two text colours the renderer picks between. Never the user's choice. */
export const TEXT_LIGHT = '#F7F5F0';
export const TEXT_DARK = '#1C1B18';

/** The renderer's canvas edge. Every geometry number below is a fraction of it. */
const CANVAS_SIZE = 1080;

/** Render pixels → fraction of the slide, at the precision fractions cross the wire in. */
function ofCanvas(pixels: number): number {
  return Math.round((pixels / CANVAS_SIZE) * 10000) / 10000;
}

/** Left edge of a full-bleed caption block, and the inset of the third templates (80/1080). */
const SAFE_AREA = ofCanvas(80);

/** Width of a full-bleed caption block (920/1080), and of a column beside a slot (428/1080). */
const FULL_BLEED_WIDTH = ofCanvas(920);
const COLUMN_WIDTH = ofCanvas(428);

/** The renderer's neutral panel, used when a theme carries no `panel_fill`. */
export const PANEL_FALLBACK = '#2B2A28';

/** The global headline cap, and the tighter ones two template families impose. */
const HEADLINE_CAP = 90;
const COLUMN_HEADLINE_CAP = 60;
const STAT_HEADLINE_CAP = 12;

/** How much of the slide a caption zone claims before the scrim starts fading. */
const SCRIM_ZONE = 1 / 3;

/** The ramp the scrim fades out over (160/1080). */
const SCRIM_FADE = 0.1481;

/** Scrim alpha at rest, and after the visual guardrails asked for a corrective pass. */
const SCRIM_ALPHA = 110 / 255;
const SCRIM_ALPHA_BOOSTED = 175 / 255;

/**
 * Which family a template belongs to — the axis the picker groups on, and the
 * one thing that decides whether a picture gets a box of its own.
 */
export type SlideTemplateKind = 'anchor' | 'slot' | 'content' | 'item' | 'community';

/** A box on the slide, in fractions of it. */
export interface TemplateBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where the caption block's own box sits against the slide. */
export interface SlideTemplateConfig {
  kind: SlideTemplateKind;
  align: 'left' | 'center';
  /**
   * Where inside its own `region` the block sits. `center` is centred in the
   * region, which for a full-bleed template is the whole canvas — so the three
   * original anchors behave exactly as they did before regions existed.
   */
  anchorY: 'top' | 'center' | 'bottom';
  label: string;
  /** One line for the picker, describing what the layout does. */
  blurb: string;
  /** The caption column: left edge and wrap width, as fractions of the slide. */
  textBox: { left: number; width: number };
  /** The vertical band the block is placed inside — the whole slide unless a picture takes half. */
  region: { top: number; bottom: number };
  /** The picture's own box, or null when the background covers the canvas. */
  imageBox: TemplateBox | null;
  /** First rung of the renderer's headline ladder, and the body size, as fractions. */
  headlineSize: number;
  bodySize: number;
  /** How many lines the headline may take. One is a stat's numeral, which never wraps. */
  maxLines: number;
  /** The renderer upper-cases every headline except the two content templates'. */
  uppercase: boolean;
  /** A pull-quote opens with a large quotation mark in the theme's accent. */
  quoteMark: boolean;
  /** `quote` is a card layout by definition, whatever the theme says. */
  forceCard: boolean;
  /** Headline length this template's own shape allows. Advisory: the server accepts 90 regardless. */
  headlineCap: number;
  /**
   * The template's gradient scrim, as a CSS `background` value.
   * `alphaOverride` substitutes the flat alpha (used by the frosted overlay's
   * lighter tint) without changing the ramp's shape.
   */
  scrimCss: (scrimColor: string, boosted: boolean, alphaOverride?: number) => string;
}

/** The full-bleed column the three anchors, `auto` and both content templates share. */
const FULL_BLEED_TEXT_BOX = { left: SAFE_AREA, width: FULL_BLEED_WIDTH } as const;

/** The whole canvas, which is the region of every template without a picture slot. */
const WHOLE_CANVAS = { top: 0, bottom: 1 } as const;

/** `#1C1B18` → `28, 27, 24`, so an alpha can be attached to it. */
function rgbChannels(hex: string): string {
  const value = hex.replace('#', '');
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  if (Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue)) return '0, 0, 0';
  return `${red}, ${green}, ${blue}`;
}

function alphaFor(boosted: boolean): number {
  return Math.round((boosted ? SCRIM_ALPHA_BOOSTED : SCRIM_ALPHA) * 100) / 100;
}

function percent(fraction: number): string {
  return `${Math.round(fraction * 1000) / 10}%`;
}

/** Solid over the caption zone at one edge, then a 160px ramp to nothing. */
function edgeScrim(
  direction: 'to top' | 'to bottom',
  scrimColor: string,
  boosted: boolean,
  alphaOverride?: number,
): string {
  const channels = rgbChannels(scrimColor);
  const alpha = alphaOverride ?? alphaFor(boosted);
  return (
    `linear-gradient(${direction}, ` +
    `rgba(${channels}, ${alpha}) 0%, ` +
    `rgba(${channels}, ${alpha}) ${percent(SCRIM_ZONE)}, ` +
    `rgba(${channels}, 0) ${percent(SCRIM_ZONE + SCRIM_FADE)})`
  );
}

/** A band across the middle, ramping in and out over the same 160px. */
function bandScrim(scrimColor: string, boosted: boolean, alphaOverride?: number): string {
  const channels = rgbChannels(scrimColor);
  const alpha = alphaOverride ?? alphaFor(boosted);
  const start = 0.5 - SCRIM_ZONE / 2;
  const end = 0.5 + SCRIM_ZONE / 2;
  return (
    'linear-gradient(to bottom, ' +
    `rgba(${channels}, 0) ${percent(start - SCRIM_FADE)}, ` +
    `rgba(${channels}, ${alpha}) ${percent(start)}, ` +
    `rgba(${channels}, ${alpha}) ${percent(end)}, ` +
    `rgba(${channels}, 0) ${percent(end + SCRIM_FADE)})`
  );
}

/**
 * The nine values `layout_template` can hold, and the geometry each one draws
 * with. A `Record` on purpose: adding a value to `SlideLayoutTemplate` without
 * describing it here is a compile error, not a silently missing preview.
 *
 * `auto` is not a layout of its own — it is the request to let the designer
 * choose, and it previews as the renderer's classic block (left margin,
 * vertically centred) because that is what an unplaced slide starts as.
 */
export const SLIDE_TEMPLATES: Record<SlideLayoutTemplate, SlideTemplateConfig> = {
  centered: {
    kind: 'anchor',
    align: 'center',
    anchorY: 'center',
    label: 'Centered',
    blurb: 'Block centred on the slide, lines centred inside it.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(76),
    bodySize: ofCanvas(37),
    maxLines: 3,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  lower_third: {
    kind: 'anchor',
    align: 'left',
    anchorY: 'bottom',
    label: 'Lower third',
    blurb: 'Caption sits at the foot of the slide, above the safe margin.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(76),
    bodySize: ofCanvas(37),
    maxLines: 3,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: (scrimColor, boosted, alphaOverride) =>
      edgeScrim('to top', scrimColor, boosted, alphaOverride),
  },
  upper_third: {
    kind: 'anchor',
    align: 'left',
    anchorY: 'top',
    label: 'Upper third',
    blurb: 'Caption sits at the head of the slide, below the safe margin.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(76),
    bodySize: ofCanvas(37),
    maxLines: 3,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: (scrimColor, boosted, alphaOverride) =>
      edgeScrim('to bottom', scrimColor, boosted, alphaOverride),
  },
  auto: {
    kind: 'anchor',
    align: 'left',
    anchorY: 'center',
    label: 'Auto',
    blurb: 'Let us place it on the next render.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(76),
    bodySize: ofCanvas(37),
    maxLines: 3,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  image_top: {
    kind: 'slot',
    align: 'left',
    anchorY: 'center',
    label: 'Image top',
    blurb: 'Picture across the top, caption underneath on a brand panel.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: { top: ofCanvas(560), bottom: 1 },
    imageBox: { left: 0, top: 0, width: 1, height: ofCanvas(560) },
    headlineSize: ofCanvas(66),
    bodySize: ofCanvas(34),
    maxLines: 3,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  image_left: {
    kind: 'slot',
    align: 'left',
    anchorY: 'center',
    label: 'Image left',
    blurb: 'Picture down the left half, caption in a column beside it.',
    textBox: { left: ofCanvas(596), width: COLUMN_WIDTH },
    region: WHOLE_CANVAS,
    imageBox: { left: 0, top: 0, width: 0.5, height: 1 },
    headlineSize: ofCanvas(52),
    bodySize: ofCanvas(30),
    maxLines: 4,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: COLUMN_HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  image_right: {
    kind: 'slot',
    align: 'left',
    anchorY: 'center',
    label: 'Image right',
    blurb: 'Picture down the right half, caption in a column beside it.',
    textBox: { left: ofCanvas(56), width: COLUMN_WIDTH },
    region: WHOLE_CANVAS,
    imageBox: { left: 0.5, top: 0, width: 0.5, height: 1 },
    headlineSize: ofCanvas(52),
    bodySize: ofCanvas(30),
    maxLines: 4,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: COLUMN_HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  quote: {
    kind: 'content',
    align: 'center',
    anchorY: 'center',
    label: 'Quote',
    blurb: 'A pull-quote in sentence case on a card, attribution underneath.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(58),
    bodySize: ofCanvas(32),
    maxLines: 4,
    uppercase: false,
    quoteMark: true,
    forceCard: true,
    headlineCap: HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  stat: {
    kind: 'content',
    align: 'center',
    anchorY: 'center',
    label: 'Stat',
    blurb: 'One big number, and what it measures underneath.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(168),
    bodySize: ofCanvas(34),
    maxLines: 1,
    uppercase: false,
    quoteMark: false,
    forceCard: false,
    headlineCap: STAT_HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  // The item templates draw their portraits, rows and grids server-side; the
  // canvas previews only the caption block, and the editor's item panel says
  // so. Their geometry mirrors TEMPLATE_GEOMETRY, like every row above.
  profile: {
    kind: 'item',
    align: 'center',
    anchorY: 'center',
    label: 'Portrait',
    blurb: 'A circle portrait over the name and designation. Team posts, product heroes.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(54),
    bodySize: ofCanvas(30),
    maxLines: 2,
    uppercase: false,
    quoteMark: false,
    forceCard: false,
    headlineCap: 60,
    scrimCss: bandScrim,
  },
  offer: {
    kind: 'item',
    align: 'center',
    anchorY: 'center',
    label: 'Offer',
    blurb: 'The deal in display type, benefits ticked off underneath.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(132),
    bodySize: ofCanvas(34),
    maxLines: 2,
    uppercase: false,
    quoteMark: false,
    forceCard: false,
    headlineCap: 24,
    scrimCss: bandScrim,
  },
  profile_grid: {
    kind: 'item',
    align: 'center',
    anchorY: 'center',
    label: 'Team grid',
    blurb: 'Rows of portraits with names and roles under a title.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(54),
    bodySize: ofCanvas(30),
    maxLines: 2,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: 60,
    scrimCss: bandScrim,
  },
  menu_list: {
    kind: 'item',
    align: 'left',
    anchorY: 'center',
    label: 'Menu',
    blurb: 'Dishes down the left, dotted leaders, prices on the right.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(66),
    bodySize: ofCanvas(30),
    maxLines: 2,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: 40,
    scrimCss: bandScrim,
  },
  // The anniversary poster: photo arch bottom-left, stacked name in the right
  // column, quote at the top, milestone badge. The text box here is the name
  // column — the block `text_position` moves — and the rest of the
  // composition is the canvas's own approximation.
  anniversary: {
    kind: 'item',
    align: 'left',
    anchorY: 'center',
    label: 'Anniversary',
    blurb: 'A milestone poster: the photo, the name stacked large, the years on a badge.',
    textBox: { left: ofCanvas(610), width: ofCanvas(390) },
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(54),
    bodySize: ofCanvas(30),
    maxLines: 2,
    uppercase: false,
    quoteMark: false,
    forceCard: false,
    headlineCap: 60,
    scrimCss: bandScrim,
  },
  // A member's own design is the canvas, and its zone map places every layer,
  // so there is no geometry of ours to mirror here — these numbers are the
  // classic block, which is what the approximate preview falls back to. The
  // renderer reads the template's zones, not this row.
  community: {
    kind: 'community',
    align: 'left',
    anchorY: 'center',
    label: 'Community',
    blurb: "A member's own design, with the layers placed by its zone map.",
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(76),
    bodySize: ofCanvas(37),
    maxLines: 3,
    // The community renderer sets its headline in caps like the anchors do.
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  // The series templates (V2): the deck's grammar. Full-bleed like the
  // anchors, so they share the anchors' column and family; the geometry
  // mirrors TEMPLATE_GEOMETRY's 'hook', 'list' and 'closer' rows.
  hook: {
    kind: 'anchor',
    align: 'center',
    anchorY: 'center',
    label: 'Hook',
    blurb: 'The cover: the headline fills the frame in two lines, no body.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(112),
    bodySize: ofCanvas(34),
    maxLines: 2,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: 48,
    scrimCss: bandScrim,
  },
  list: {
    kind: 'anchor',
    align: 'left',
    anchorY: 'center',
    label: 'List',
    blurb: 'Headline over the body set as numbered rows.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(58),
    bodySize: ofCanvas(32),
    maxLines: 2,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: HEADLINE_CAP,
    scrimCss: bandScrim,
  },
  closer: {
    kind: 'anchor',
    align: 'center',
    anchorY: 'center',
    label: 'Closer',
    blurb: 'The closing frame: one line, the logo set large.',
    textBox: FULL_BLEED_TEXT_BOX,
    region: WHOLE_CANVAS,
    imageBox: null,
    headlineSize: ofCanvas(72),
    bodySize: ofCanvas(34),
    maxLines: 2,
    uppercase: true,
    quoteMark: false,
    forceCard: false,
    headlineCap: 60,
    scrimCss: bandScrim,
  },
};

/** The quotation mark a `quote` slide opens with (140/1080, in the theme accent). */
export const QUOTE_MARK_FRACTION = ofCanvas(140);

/** Picker order: the anchors and the hand-back, then the slots, then the content pair. */
export const SLIDE_TEMPLATE_ORDER: readonly SlideLayoutTemplate[] = [
  'centered',
  'lower_third',
  'upper_third',
  'auto',
  'hook',
  'list',
  'closer',
  'image_top',
  'image_left',
  'image_right',
  'quote',
  'stat',
  'profile',
  'profile_grid',
  'menu_list',
  'offer',
  'anniversary',
  'community',
];

/** One group of the picker: a family, its heading, and its templates in order. */
export interface SlideTemplateGroup {
  kind: SlideTemplateKind;
  label: string;
  templates: readonly SlideLayoutTemplate[];
}

/** The templates of one family, in picker order. */
function templatesOfKind(kind: SlideTemplateKind): readonly SlideLayoutTemplate[] {
  return SLIDE_TEMPLATE_ORDER.filter((value) => SLIDE_TEMPLATES[value].kind === kind);
}

/**
 * The picker's three groups, in `SLIDE_TEMPLATE_ORDER`. Nine flat options ask
 * the user to read nine blurbs to find the one difference that matters; the
 * family is that difference, so it is what the eye lands on first.
 */
export const SLIDE_TEMPLATE_GROUPS: readonly SlideTemplateGroup[] = [
  { kind: 'anchor', label: 'Caption on the picture', templates: templatesOfKind('anchor') },
  { kind: 'slot', label: 'Picture beside the caption', templates: templatesOfKind('slot') },
  { kind: 'content', label: 'By content', templates: templatesOfKind('content') },
  // The item layouts draw structured rows (members, dishes, benefits) that
  // only exist on a slide that has items — but a reviewer may still move a
  // slide between them, so they are on the picker like the rest.
  { kind: 'item', label: 'Item layouts', templates: templatesOfKind('item') },
  // `community` is deliberately absent: a slide is drawn on a member's uploaded
  // design because the brief named that template, and switching an ordinary
  // slide into it would name no design to draw on. There is no group for it, so
  // the picker cannot offer it.
];

/**
 * The headline length a template's own shape allows — the global 90 unless the
 * template is tighter (a stat headline is a numeral; a column beside a picture
 * has half the width to spend). Advisory on this side: the server accepts up to
 * 90 whatever the template and answers an over-cap headline with a guardrail
 * finding rather than a refusal, so the editor shows the number and never
 * blocks the save.
 */
export function headlineCapFor(template: SlideLayoutTemplate): number {
  return SLIDE_TEMPLATES[template].headlineCap;
}

/** True when this template fits its picture into a box instead of over the whole canvas. */
export function hasImageSlot(template: SlideLayoutTemplate): boolean {
  return SLIDE_TEMPLATES[template].imageBox !== null;
}

/**
 * The scrim is always the opposite of the text, exactly as the renderer picks
 * it — dark panel under light words, light panel under dark ones.
 */
export function scrimColorFor(textColor: string): string {
  return textColor.toUpperCase() === TEXT_LIGHT ? TEXT_DARK : TEXT_LIGHT;
}

/** The frosted band's tint alpha (70/255) — the blur does most of the work. */
const FROSTED_TINT_ALPHA = 70 / 255;

/** The duotone preview's flat accent tint (V2) — a stand-in for the gradient map. */
const DUOTONE_TINT_ALPHA = 0.28;

/**
 * The scrim layer's CSS for one overlay style — an approximation, like the
 * whole stage. `faded` is the classic scrim; `frosted` pairs a lighter tint
 * with a `backdrop-filter` blur on the same element; `clear` draws nothing
 * unless the render actually fell back to its corrective panel
 * (`scrim_applied`), in which case the truth wins over the preference.
 * `duotone` tints the whole picture in the brand colours and lays no band; the
 * preview approximates it as a flat wash of `accent` at low alpha (no accent,
 * nothing), and like `clear` the corrective panel wins when it was applied.
 */
export function overlayCss(
  style: SlideOverlayStyle,
  template: SlideTemplateConfig,
  scrimColor: string,
  boosted: boolean,
  accent?: string,
): { background: string; backdropFilter?: string } {
  if (style === 'clear' && !boosted) return { background: 'none' };
  if (style === 'duotone') {
    if (boosted) return { background: template.scrimCss(scrimColor, true) };
    if (accent === undefined) return { background: 'none' };
    return { background: `rgba(${rgbChannels(accent)}, ${DUOTONE_TINT_ALPHA})` };
  }
  if (style === 'frosted') {
    const tint = boosted
      ? template.scrimCss(scrimColor, true)
      : template.scrimCss(scrimColor, false, Math.round(FROSTED_TINT_ALPHA * 100) / 100);
    return { background: tint, backdropFilter: 'blur(4px)' };
  }
  return { background: template.scrimCss(scrimColor, boosted) };
}

/** Card geometry, as fractions of the 1080 canvas (radius 32, padding 24). */
export const CARD_RADIUS = 32 / 1080;
export const CARD_PADDING = 24 / 1080;

/** The card's fill: the theme's own, or the scrim rule (opposite of the text). */
export function cardFillCss(theme: SlideTheme, textColor: string): string {
  const fill = theme.card_fill ?? scrimColorFor(textColor);
  return `rgba(${rgbChannels(fill)}, ${Math.round((theme.card_fill_alpha / 255) * 100) / 100})`;
}

/** Eyebrow type, mirrored from the renderer (26/1080, 3px tracking, 18px gap). */
export const EYEBROW_FONT_FRACTION = 26 / 1080;
export const EYEBROW_TRACKING_FRACTION = 3 / 1080;
export const EYEBROW_GAP_FRACTION = 18 / 1080;

/** The renderer drops a shadow only under its light text. */
export function hasTextShadow(textColor: string): boolean {
  return textColor.toUpperCase() === TEXT_LIGHT;
}

/** Inset a template keeps from the top, bottom and left edges (80/1080). */
export const TEMPLATE_SAFE_AREA = SAFE_AREA;
